import * as i0 from '@angular/core';
import { Injectable, Pipe, Injector, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule } from '@angular/core';
import { Subject, ReplaySubject, BehaviorSubject, isObservable, of, combineLatest, fromEvent } from 'rxjs';
import * as i1 from '@angular/common';
import { KeyValuePipe, AsyncPipe, NgTemplateOutlet, SlicePipe, NgClass, NgIf, NgForOf, PercentPipe, CommonModule } from '@angular/common';
import { distinctUntilChanged, tap, shareReplay, startWith, map, switchMap, withLatestFrom, filter, pluck, take, takeUntil } from 'rxjs/operators';

class CoreService {
    constructor() { }
}
CoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

let dashed;
dashed = (s) => s.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
let capitalize;
capitalize = (s) => (s.charAt(0).toUpperCase() + s.slice(1))
    .replace(/_/g, ' ')
    .replace(/([A-Z][a-z]+)/g, ' $1')
    .replace(/([A-Z]{2,})/g, ' $1')
    .replace(/\s{2,}/g, ' ')
    .trim();
let chunk;
chunk = (array, chunkSize) => {
    if (chunkSize < 0) {
        throw new Error('Invalid chunk size');
    }
    if (chunkSize === 0 && !chunkSize) {
        return [array];
    }
    const CHUNK = [];
    for (let i = 0, len = array.length; i < len; i += chunkSize) {
        CHUNK.push(array.slice(i, i + chunkSize));
    }
    return CHUNK;
};
let search;
search = (text, caseSensitive, data, config) => {
    if (config.columns) {
        const searchColumns = Object.keys(config.columns).filter(
        // @ts-ignore
        (key) => config.columns &&
            !config.columns[key]?.hidden &&
            config.columns[key]?.search !== false);
        const FILTERED = [];
        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            const match = Object.entries(row)
                .filter(([key, value]) => searchColumns.indexOf(key) !== -1)
                .reduce((acc, [key, value], index) => {
                const search = config?.columns[key]?.search;
                // if search is a function...
                if (typeof search === 'function') {
                    // ...use search function to return value to search
                    value = search(row, key, value);
                }
                return (acc +
                    (index === 0 ? '' : ' ? ') +
                    (caseSensitive ? value + '' : (value + '').toLowerCase()));
            }, '')
                .indexOf(caseSensitive ? text : text.toLowerCase()) !== -1;
            if (match) {
                FILTERED[FILTERED.length] = row;
            }
        }
        return FILTERED;
    }
    else {
        return data;
    }
};
let calculate = (data, config) => {
    const CALCULATIONS = {};
    const COLUMN_CALCULATIONS = {};
    let CALCULATED = Object.entries(config.footer?.columns || {})
        .filter(([columnName, calculations]) => Object.values(calculations || {}).filter((value) => value !== false)
        .length >= 0)
        .reduce((acc, [columnName, calculations], index) => {
        acc[columnName] = Object.entries(calculations || {})
            .filter(([calculation, value]) => value !== false)
            .reduce((acc, [calculation, value]) => {
            if (COLUMN_CALCULATIONS[columnName]) {
                COLUMN_CALCULATIONS[columnName].push(calculation);
            }
            else if (value === true) {
                COLUMN_CALCULATIONS[columnName] = [calculation];
            }
            CALCULATIONS[calculation] = true;
            if (typeof value === 'function') {
                value = value(data, columnName);
            }
            acc[calculation] = value === true ? 0 : value;
            return acc;
        }, {});
        return acc;
    }, {});
    if (Object.keys(CALCULATED).length > 0) {
        for (let i = 0; i < data.length; i++) {
            Object.entries(COLUMN_CALCULATIONS).forEach(([column, calculations]) => {
                if (calculations.indexOf('sum') > -1) {
                    CALCULATED[column].sum += data[i][column];
                }
                if (calculations.indexOf('avg') > -1 &&
                    calculations.indexOf('sum') === -1) {
                    if (CALCULATED[column].sum === undefined) {
                        CALCULATED[column].sum = 0;
                    }
                    CALCULATED[column].sum += data[i][column];
                }
                if (calculations.indexOf('max') > -1 &&
                    (!CALCULATED[column].max || +data[i][column] > CALCULATED[column].max)) {
                    CALCULATED[column].max = +data[i][column];
                }
                if (calculations.indexOf('min') > -1 &&
                    (!CALCULATED[column].min || +data[i][column] < CALCULATED[column].min)) {
                    CALCULATED[column].min = +data[i][column];
                }
            });
        }
        Object.entries(COLUMN_CALCULATIONS).forEach(([column, calculations]) => {
            if (calculations.indexOf('avg') !== -1) {
                CALCULATED[column].avg = CALCULATED[column]?.sum / data.length;
                // if sum is not part of calculations config...
                if (calculations.indexOf('sum') === -1 && CALCULATED[column].sum) {
                    // ...remove it
                    delete CALCULATED[column].sum;
                }
            }
            if (calculations.indexOf('count') !== -1) {
                CALCULATED[column].count = data.length;
            }
        });
    }
    return {
        calculated: CALCULATED,
        calculations: Object.keys(CALCULATIONS).sort((a, b) => (config.footer?.rowOrder?.indexOf(a) || 0) -
            (config.footer?.rowOrder?.indexOf(b) || 0)),
        calculatedColumnsCount: Object.keys(CALCULATED).length || 0,
    };
};
/** sortOnMultipleKeys
 * @description Sort data on multiple keys
 * @param {GtSortOrder} keys - array with sort config objects to sort on, data will be sorted according to array order
 * @returns sort function
 */
const sortOnMultipleKeys = (keys) => {
    const order = keys.map((key) => (key.order === 'desc' ? -1 : 1));
    return (a, b) => {
        for (let i = 0; i < keys.length; i++) {
            const o = keys[i].key;
            if (a[o] === b[o])
                return 0;
            if (a[o] === null)
                return 1;
            if (b[o] === null)
                return -1;
            if (a[o] > b[o])
                return order[i];
            if (a[o] < b[o])
                return -order[i];
        }
        return 0;
    };
};
/** parseSortOrderParams
 * @description Convert sort order query param to array with sort config objects
 * @param sortParams - Query param string where each sort config object is separated by comma and order is indicated by + (ascending) or - (descending), e.g. _'name,-age'_
 * @returns GtSortOrder - Array with sort config objects
 */
const parseSortOrderParams = (sortParams) => {
    const sortParamsArray = sortParams.split(',');
    return sortParamsArray.map((sortParam) => {
        const [key, order] = sortParam.split(':');
        return {
            key: key.replace(/^[+-]/, ''),
            order: order === 'desc' ? 'desc' : 'asc',
        };
    });
};
/** sortOrderConfigToParam
 * @description Convert sort config object to string that can be used as query param when sorting is implemented server side
 * @param sortConfig - Sort config object
 * @returns string - Query param string where order is indicated by + (ascending) or - (descending), e.g. _'-name'_
 */
const sortOrderConfigToParam = (sortConfig) => {
    const order = sortConfig.order === 'desc' ? '-' : '+';
    return `${order}${sortConfig.key}`;
};
/** sortOrderToParams
 * @description Convert sort order array to string that can be used as query param when sorting is implemented server side
 * @param sortOrder - Array with sort config objects
 * @returns string - Query param string where each sort config object is separated by comma and order is indicated by + (ascending) or - (descending), e.g. _'name,-age'_
 */
const sortOrderToParams = (sortOrder) => {
    return sortOrder
        .map((sortConfig) => sortOrderConfigToParam(sortConfig))
        .join(',');
};

class CapitalCasePipe {
    transform(s) {
        return capitalize(s);
    }
}
CapitalCasePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CapitalCasePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CapitalCasePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: CapitalCasePipe, isStandalone: true, name: "capitalCase" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CapitalCasePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'capitalCase',
                    standalone: true,
                }]
        }] });

class SortClassPipe {
    transform(sortOrder, key, context = 'class') {
        const sortIndex = sortOrder
            ? sortOrder.findIndex((s) => s.key === key)
            : -1;
        if (context === 'aria') {
            if (sortIndex === -1 || !sortOrder) {
                return null;
            }
            else {
                return `${sortOrder[sortIndex].order}ending`;
            }
        }
        else if (context === 'class') {
            if (sortIndex === -1 || !sortOrder) {
                return '';
            }
            else {
                return `gt-sort-${sortOrder[sortIndex].order}`;
            }
        }
        else {
            return (sortOrder && sortOrder?.length === 1) || sortIndex < 0
                ? null
                : sortIndex + 1 + '';
        }
    }
}
SortClassPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: SortClassPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SortClassPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: SortClassPipe, isStandalone: true, name: "sortClass" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: SortClassPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sortClass',
                    standalone: true,
                }]
        }] });

class DashCasePipe {
    transform(s) {
        return dashed(s);
    }
}
DashCasePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: DashCasePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DashCasePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: DashCasePipe, isStandalone: true, name: "dashCase" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: DashCasePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dashCase',
                    standalone: true,
                }]
        }] });

class DynamicPipe {
    constructor(injector) {
        this.injector = injector;
    }
    transform(value, requiredPipe, pipeArgs) {
        const injector = Injector.create({
            name: 'DynamicPipe',
            parent: this.injector,
            providers: [{ provide: requiredPipe }],
        });
        const pipe = injector.get(requiredPipe);
        return pipe.transform(value, ...(pipeArgs || []));
    }
}
DynamicPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: DynamicPipe, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Pipe });
DynamicPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: DynamicPipe, isStandalone: true, name: "dynamicPipe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: DynamicPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dynamicPipe',
                    standalone: true,
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class HighlightPipe {
    transform(text, searchTerm) {
        if (!searchTerm) {
            return text;
        }
        const haystackAlwaysString = text + '';
        let highlightedText = haystackAlwaysString; // fallback
        let searchPattern;
        try {
            searchPattern = new RegExp('(' +
                // @ts-ignore
                searchTerm
                    .toLowerCase()
                    .match(/".*?"|[^ ]+/g) // extract words
                    .map((needle) => needle.replace(/"(.*?)"/, '$1') // strip away '"'
                )
                    .join('|') + // combine words
                ')', 'ig');
        }
        catch (error) {
            return highlightedText;
        }
        const containsTagPattern = /(<.*?>)(.*)(<\/.*?>)/gi;
        const containsTagMatches = containsTagPattern.exec(haystackAlwaysString);
        if (containsTagMatches) {
            // tag exists in haystack
            highlightedText =
                containsTagMatches[1] +
                    containsTagMatches[2].replace(searchPattern, '<span class="gt-highlight-search">$1</span>') +
                    containsTagMatches[3];
        }
        else {
            highlightedText = haystackAlwaysString.replace(searchPattern, '<span class="gt-highlight-search">$1</span>');
        }
        return highlightedText;
    }
}
HighlightPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: HighlightPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HighlightPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: HighlightPipe, isStandalone: true, name: "highlight" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: HighlightPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlight',
                    standalone: true,
                }]
        }] });

class RowSelectionPipe {
    transform(row, selection, comparator, className) {
        return className && comparator(row, selection) ? className : '';
    }
}
RowSelectionPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: RowSelectionPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
RowSelectionPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: RowSelectionPipe, isStandalone: true, name: "rowSelection" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: RowSelectionPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rowSelection',
                    standalone: true,
                }]
        }] });

class CoreComponent {
    constructor() {
        this.unsubscribe$ = new Subject();
        this._navigationKeys = [
            'ArrowDown',
            'ArrowUp',
            'ArrowLeft',
            'ArrowRight',
            'Home',
            'End',
        ];
        this._selectKeys = ['Enter', ' '];
        this._customClasses = {
            selectedRow: 'gt-selected',
            activeRow: 'gt-active',
        };
        /** selection
         * @description An object that contains the currently selected row(s) in the table. It's passed to the selection function to determine which rows should be selected.
         * @type {any}
         */
        this.selection = {};
        /** generateRowId
         * @description Whether or not to generate a unique id for each row in the table. Defaults to `true`.
         * @type {boolean}
         */
        this.generateRowId = true;
        this.rowClick = new EventEmitter();
        this.rowSelect = new EventEmitter();
        this.sortOrderChange = new EventEmitter();
        this._rowActive$ = new ReplaySubject(1);
        this.rowActive = new EventEmitter();
        this.columnSort = new EventEmitter();
        /** page change event - emitted when current page/index changes for pagination */
        this.pageChange = new EventEmitter();
        this.rowActive$ = this._rowActive$.asObservable().pipe(distinctUntilChanged((p, q) => {
            if (this.rowIdKey && p.row && q.row) {
                return p.row[this.rowIdKey] === q.row[this.rowIdKey];
            }
            else if (this.generateRowId && p.row && q.row) {
                return p.row._id === q.row._id;
            }
            else {
                return p.index === q.index;
            }
        }), tap((event) => (this.activeRowIndex = event.index)), tap((event) => this.rowActive.emit(event)), shareReplay(1));
        this.activeRowIndex = null;
        this._loading$ = new ReplaySubject(1);
        this._sortOrder$ = new BehaviorSubject([]);
        this._searchBy$ = new ReplaySubject(1);
        this.searchBy$ = this._searchBy$.pipe(startWith(''), map((value) => (isObservable(value) ? value : of(value))), switchMap((obs) => obs), shareReplay(1));
        this._pagingInfo$ = new BehaviorSubject({
            pageCurrent: null,
            pageNext: null,
            pagePrevious: null,
            pageSize: null,
            numberOfRecords: null,
            //recordsAfterFilter: null,
            //recordsAfterSearch: null,
            //recordsAll: null,
        });
        // tslint:disable-next-line:variable-name
        this._tableConfig$ = new BehaviorSubject({});
        this.tableConfig$ = this._tableConfig$.pipe(map((value) => (isObservable(value) ? value : of(value))), switchMap((obs) => obs), tap((config) => (this._tableConfig = config)), shareReplay(1));
        this._data$ = new ReplaySubject(1);
        this.data$ = this._data$.pipe(map((value) => (isObservable(value) ? value : of(value))), switchMap((obs) => combineLatest([obs])), withLatestFrom(this.tableConfig$), map(([[data], config]) => {
            // if columns or rows contains config for mapTo...
            if ((config.columns &&
                !!Object.values(config.columns).find((column) => !!column.mapTo)) ||
                (config.rows &&
                    !!Object.values(config.rows).find((column) => !!column.mapTo))) {
                // ...map data to new keys on row...
                const newData = [];
                for (let i = 0; i < data.length; i++) {
                    const row = data[i];
                    const newKeys = Object.entries(config.columns || config.rows || [])
                        .filter(([key, value]) => !!value.mapTo) // add keys for columns with mapTo config...
                        .reduce((previousValue, [key, value]) => ({
                        ...previousValue,
                        [key]: this.nestedValue(row, value.mapTo.path, value.mapTo?.missingValue),
                    }), {});
                    newData[i] = { ...row, ...newKeys };
                }
                data = newData;
            }
            if (this.generateRowId && !this.rowIdKey && data.length > 0) {
                const dataWithId = [];
                for (let i = 0; i < data.length; i++) {
                    dataWithId[i] = { ...data[i], _id: i };
                }
                data = dataWithId;
            }
            return { data, config };
        }), switchMap((obs) => combineLatest([of(obs), this.sortOrder$, this.searchBy$])), map(([table, sortBy, searchBy]) => {
            // create a new array reference and sort new array (prevent mutating existing state)
            table.data = [...table.data];
            return !sortBy?.length || table.config?.disableTableSort
                ? searchBy && !this.tableInfo?.lazyLoaded
                    ? search(searchBy, false, table.data, table.config)
                    : table.data
                : searchBy && !this.tableInfo?.lazyLoaded
                    ? search(searchBy, false, table.data, table.config)?.sort(sortOnMultipleKeys(sortBy))
                    : table.data?.sort(sortOnMultipleKeys(sortBy));
        }), shareReplay(1));
        this.calculations$ = combineLatest([this.data$, this.tableConfig$]).pipe(map(([data, config]) => calculate(data, config)), shareReplay(1));
        this.table$ = combineLatest([
            this.data$,
            this.tableConfig$,
            this._pagingInfo$,
        ]).pipe(map(([sorted, config, pagingInfo]) => {
            if (pagingInfo.pageCurrent !== null &&
                pagingInfo.numberOfRecords !== null &&
                pagingInfo.pageSize !== null) {
                return {
                    data: [sorted],
                    config,
                    info: {
                        lazyLoaded: true,
                        numberOfRecords: pagingInfo.numberOfRecords,
                        pageSize: pagingInfo.pageSize,
                        pageTotal: pagingInfo.pageTotal ??
                            Math.ceil(pagingInfo.numberOfRecords / pagingInfo.pageSize),
                    },
                };
            }
            // if pagination is disabled...
            if (!config.pagination || config.pagination.length === 0) {
                // ...return unaltered array
                return {
                    data: [sorted],
                    config,
                    info: { numberOfRecords: sorted.length, pageTotal: 1 },
                };
            }
            // return record set
            return {
                data: chunk(sorted, +(config.pagination.length || 0)),
                config,
                info: {
                    numberOfRecords: sorted.length,
                    pageSize: +(config.pagination.length || 0),
                    pageTotal: Math.ceil(sorted.length / +(config.pagination.length || 0)),
                },
            };
        }), tap((meta) => this._tableInfo$.next(meta.info)), shareReplay(1));
        this._tableInfo$ = new BehaviorSubject(undefined);
        this._currentPaginationIndex$ = new BehaviorSubject(0);
        this.currentPaginationIndex$ = combineLatest([
            this._currentPaginationIndex$,
            this.table$,
        ]).pipe(map(([page, table]) => {
            // determine last page
            const lastPage = Math.ceil(table.info.numberOfRecords /
                (table.info.pageSize ??
                    (table.config?.pagination?.length || table.info.numberOfRecords))) - 1;
            // determine min/max position
            return +page < 0 ? 0 : +page > lastPage ? lastPage : +page;
        }), distinctUntilChanged(), tap((index) => this.pageChange.emit({ index })), shareReplay(1));
        this.colspan$ = this.tableConfig$.pipe(switchMap((config) => config.columns
            ? of(Object.values(config.columns || config.rows || {}).filter((value) => value.hidden !== true).length)
            : this.data$.pipe(map((data) => data.length + 1))), shareReplay(1));
        this.footerColspan$ = this.tableConfig$.pipe(map((config) => {
            let colspan = 0;
            Object.values(config?.footer?.columns || {}).forEach((calculations) => {
                if (Object.values(calculations).filter((value) => value !== false)
                    .length >= 0) {
                    colspan += 1;
                }
            }, {});
            return colspan;
        }), shareReplay(1));
        this.columnOrder = (a, b) => {
            return (a.value.order || 0) - (b.value.order || 0);
        };
        this._unsubscribeFromKeyboardEvents$ = new Subject();
        this._keyboardArrowEvent$ = fromEvent(document, 'keydown').pipe(filter((event) => [...this._navigationKeys, ...this._selectKeys].indexOf(event.key) > -1));
    }
    get navigationKeys() {
        return this._navigationKeys;
    }
    /** navigationKeys
     * @description An array of keyboard keys that will trigger navigation and active row, currently only supports arrow keys, home and end (omit key name from array to disable it)
     * @type {string[]}
     * @default ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
     */
    set navigationKeys(value) {
        this._navigationKeys = value;
    }
    get selectKeys() {
        return this._selectKeys;
    }
    /** selectKeys
     * @description An array of keyboard keys that will trigger row selection (omit key name from array to disable it)
     * @type {string[]}
     * @default ['Enter', ' ']
     */
    set selectKeys(value) {
        this._selectKeys = value;
    }
    get sortOrder$() {
        return this._sortOrder$.asObservable();
    }
    set loading(isLoading) {
        this._loading$.next(isLoading);
    }
    set paginationIndex(pageIndex) {
        this._currentPaginationIndex$.next(pageIndex);
    }
    get paginationIndex() {
        return this._currentPaginationIndex$.getValue();
    }
    set pagingInfo(value) {
        if (value) {
            this._pagingInfo$.next(value);
            if (value.pageCurrent !== this._currentPaginationIndex$.getValue() + 1 &&
                value.pageCurrent !== null) {
                this.paginationIndex = value.pageCurrent - 1;
            }
        }
    }
    /** customClasses
     * @description An object that contains custom classes for various elements in the table.
     * @type {object} - { selectedRow: string, activeRow: string } - default classes are 'gt-selected' and 'gt-active'
     */
    set customClasses(classes) {
        this._customClasses = { ...this._customClasses, ...classes };
    }
    get customClasses() {
        return this._customClasses;
    }
    /** isRowSelectedFn
     * @description Function to determine if row is selected or not.
     * @type {fn} A function that receives a row object and optional state for current selection that can be used to determine if row should be marked as selected or not. */
    set isRowSelectedFn(fn) {
        this._isRowSelectedFn = fn;
    }
    get isRowSelectedFn() {
        return this._isRowSelectedFn;
    }
    /** trackRowByFn
     * @description A function that returns a unique identifier for each row in the table to optimize rendering when data is added or removed.
     * @type fn - TrackByFunction to retrieve unique id based on index and/or row. Defaults to using `row[this.rowIdKey]`.
     */
    set trackRowByFn(fn) {
        this._trackRowByFn = fn;
    }
    get trackRowByFn() {
        return this._trackRowByFn;
    }
    _trackRowByFn(index, row) {
        return this.rowIdKey ? row[this.rowIdKey] : row?._id;
    }
    set search(string) {
        this._searchBy$.next(string);
    }
    set config(config) {
        this._tableConfig$.next(config);
    }
    set data(data) {
        if (data) {
            this._data$.next(data);
        }
    }
    set sortOrder(sortConfig) {
        if (JSON.stringify(sortConfig) !== JSON.stringify(this._sortOrder$.value)) {
            this.sortOrderChange.emit(sortConfig);
            this._sortOrder$.next(sortConfig);
        }
    }
    _rowClick(row, index, event) {
        this.rowClick.emit({ row, index, event });
    }
    _rowActive(row, index, event) {
        this.rowSelect.emit({ row, index, event });
    }
    activateRow(arg, event) {
        if (typeof arg === 'number') {
            this.table$
                .pipe(pluck('data'), map((data) => data[this.paginationIndex][arg]), take(1), takeUntil(this.unsubscribe$))
                .subscribe((row) => this._activateRow(row, arg, event));
        }
        else if (typeof arg === 'string') {
            // TODO: implement hover by id
        }
        else {
            this._activateRow(null, null);
        }
    }
    _activateRow(row, index, event) {
        this._rowActive$.next({ row, index, event });
    }
    get loading$() {
        return this._loading$.pipe(startWith(false), map((value) => (isObservable(value) ? value : of(value))), switchMap((obs) => obs), shareReplay(1));
    }
    /** tableInfo$ - returns observable for table info
     * @return Observable<TableInfo> */
    get tableInfo$() {
        return this._tableInfo$.asObservable().pipe(filter((info) => !!info), shareReplay(1));
    }
    /** tableInfo - returns the current table info
     * @return TableInfo */
    get tableInfo() {
        return this._tableInfo$.getValue();
    }
    /** sortByKey - Sort by key in table row
     * @param key - key to sort by
     * @param { MouseEvent } [$event] - Mouse event triggering sort, if shift key is pressed sort key will be added to already present sort keys
     */
    sortByKey(key, $event) {
        const shiftKey = $event?.shiftKey === true;
        const currentOrder = this._sortOrder$.value;
        let sortOrder = 'asc';
        let newOrder = [];
        // if shift key is pressed while sorting...
        if (shiftKey) {
            // ...check if key is already sorted
            const existingSortPosition = currentOrder.findIndex((value) => value.key === key);
            if (existingSortPosition === -1) {
                // ...if key is not sorted, add it to the end of the sort order
                newOrder = [...currentOrder, { key, order: 'asc' }];
            }
            else {
                // ...if key is already sorted, toggle sort order
                sortOrder = currentOrder[existingSortPosition].order;
                const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                newOrder = [...currentOrder];
                newOrder[existingSortPosition] = {
                    ...newOrder[existingSortPosition],
                    order: newSortOrder,
                };
            }
        }
        else {
            // ...else if shift key is not pressed...
            if (currentOrder.length > 0) {
                // ...check if key is already sorted
                const existingSortPosition = currentOrder.findIndex((value) => value.key === key);
                // ...if key is already sorted, toggle sort order
                if (existingSortPosition === -1) {
                    newOrder = [{ key, order: 'asc' }];
                }
                else {
                    sortOrder = currentOrder[existingSortPosition].order;
                    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                    newOrder = [{ key, order: newSortOrder }];
                }
            }
            else {
                // ...if key is not sorted set sort order for key to ascending
                newOrder = [{ key, order: sortOrder }];
            }
        }
        // create sort event
        const sortEvent = {
            key,
            order: sortOrder,
            currentSortOrder: newOrder,
            addSortKey: shiftKey,
        };
        // if event is passed to sort function...
        if ($event) {
            // ...emit it as well
            sortEvent.event = $event;
        }
        // emit sort event
        this.columnSort.emit(sortEvent);
        // if table is not lazy loaded (sorting is then handled server-side)...
        if (!this.tableInfo?.lazyLoaded) {
            // ...update sort order
            this.sortOrder = newOrder;
        }
    }
    nestedValue(object, mapTo, missingValue = null) {
        const levels = mapTo.split('.');
        return levels.reduce((previousValue, currentValue, index) => previousValue[currentValue] ||
            (index === levels.length - 1 ? missingValue : {}), object);
    }
    listenToKeyboardEvents() {
        if (!this._tableConfig?.activateRowOnKeyboardNavigation) {
            return;
        }
        this._unsubscribeFromKeyboardEvents$.next(true);
        this._keyboardArrowEvent$
            .pipe(withLatestFrom(this.data$, this.currentPaginationIndex$, this.tableInfo$), takeUntil(this._unsubscribeFromKeyboardEvents$), takeUntil(this.unsubscribe$))
            .subscribe(([event, rows, currentPage, tableInfo]) => {
            const selectEvent = this._selectKeys.includes(event.key);
            if (selectEvent && this.activeRowIndex !== null) {
                const rowIndex = this.activeRowIndex + currentPage * (tableInfo?.pageSize ?? 0);
                this._rowActive(rows[rowIndex], rowIndex, event);
                return;
            }
            const navigationEvent = this._navigationKeys.includes(event.key);
            if (navigationEvent) {
                this._handleNavigationEvent(event, rows, currentPage, tableInfo);
            }
        });
    }
    unsubscribeFromKeyboardEvents(tableRef) {
        if (!this._tableConfig?.activateRowOnKeyboardNavigation) {
            return;
        }
        // only unsubscribe if table is not focused
        if (tableRef !== document.activeElement) {
            if (this._tableConfig?.activateRowOnHover) {
                // unset active row
                this.activateRow(null);
            }
            this._unsubscribeFromKeyboardEvents$.next(true);
        }
    }
    _handleNavigationEvent(event, rows, currentPage, tableInfo) {
        const hasPagination = (tableInfo?.pageTotal || 0) > 1 && tableInfo;
        const lastRowIndex = rows.length - 1;
        let newIndex = this.activeRowIndex;
        let indexModifier = 0;
        if (event.key === 'Home') {
            this.paginationIndex = 0;
            this.activateRow(0, event);
            return;
        }
        if (event.key === 'End') {
            const indexOfLastRecord = hasPagination
                ? rows.length - (tableInfo.pageTotal - 1) * tableInfo.pageSize - 1
                : lastRowIndex;
            if (tableInfo?.pageTotal) {
                this.paginationIndex = tableInfo.pageTotal - 1;
            }
            this.activateRow(indexOfLastRecord, event);
            return;
        }
        if (event.key === 'ArrowDown') {
            indexModifier = 1;
        }
        else if (event.key === 'ArrowUp') {
            indexModifier = -1;
        }
        if (newIndex === null) {
            newIndex = 0;
        }
        else if (newIndex + indexModifier >= 0 &&
            newIndex + indexModifier <= lastRowIndex) {
            newIndex = newIndex + indexModifier;
        }
        if (hasPagination && tableInfo?.pageSize) {
            const isNotLastPage = currentPage + 1 < tableInfo.pageTotal;
            const recordsOnLastPage = rows.length - (tableInfo.pageTotal - 1) * tableInfo.pageSize - 1;
            const maxIndex = isNotLastPage
                ? tableInfo?.pageSize - 1
                : recordsOnLastPage;
            if (event.key === 'ArrowLeft' && currentPage > 0) {
                this.paginationIndex = currentPage - 1;
                this.activateRow(newIndex, event);
                return;
            }
            else if (event.key === 'ArrowRight' && isNotLastPage) {
                if (currentPage + 1 === tableInfo.pageTotal - 1 &&
                    newIndex > recordsOnLastPage) {
                    this.activateRow(recordsOnLastPage, event);
                }
                this.paginationIndex = currentPage + 1;
                this.activateRow(newIndex, event);
                return;
            }
            if (currentPage > 0 &&
                indexModifier < 0 &&
                newIndex + indexModifier <= lastRowIndex &&
                (this.activeRowIndex || 0) + indexModifier < 0) {
                // set last row of previous page as active
                this.activateRow(tableInfo?.pageSize - 1, event);
                this.paginationIndex = currentPage - 1;
                return;
            }
            const pageIndex = newIndex % tableInfo?.pageSize;
            if (newIndex > maxIndex && currentPage + 1 < tableInfo.pageTotal) {
                this.paginationIndex = currentPage + 1;
            }
            this.activateRow(pageIndex > maxIndex ? maxIndex : pageIndex, event);
            return;
        }
        this.activateRow(newIndex, event);
    }
    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
CoreComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CoreComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CoreComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.1", type: CoreComponent, isStandalone: true, selector: "angular-generic-table", inputs: { navigationKeys: "navigationKeys", selectKeys: "selectKeys", loading: "loading", paginationIndex: "paginationIndex", pagingInfo: "pagingInfo", customClasses: "customClasses", isRowSelectedFn: "isRowSelectedFn", selection: "selection", rowIdKey: "rowIdKey", generateRowId: "generateRowId", trackRowByFn: "trackRowByFn", search: "search", config: "config", data: "data", sortOrder: "sortOrder" }, outputs: { rowClick: "rowClick", rowSelect: "rowSelect", sortOrderChange: "sortOrderChange", rowActive: "rowActive", columnSort: "columnSort", pageChange: "pageChange" }, ngImport: i0, template: "<table\n  [ngClass]=\"(tableConfig$ | async)?.class || 'table'\"\n  [class.table-mobile]=\"(tableConfig$ | async)?.mobileLayout\"\n  [class.table-horizontal]=\"(tableConfig$ | async)?.rows\"\n  [class.table-loading]=\"loading$ | async\"\n  [class.gt-sticky-row-header]=\"\n    (tableConfig$ | async)?.stickyHeaders?.row && (tableConfig$ | async)?.rows\n  \"\n  [class.gt-sticky-column-header]=\"\n    (tableConfig$ | async)?.stickyHeaders?.column\n  \"\n  [attr.aria-busy]=\"(loading$ | async) === true ? true : null\"\n  [tabindex]=\"(tableConfig$ | async)?.activateRowOnKeyboardNavigation ? 0 : -1\"\n  #tableRef\n  (focus)=\"listenToKeyboardEvents()\"\n  (focusout)=\"unsubscribeFromKeyboardEvents(tableRef)\"\n  (mouseenter)=\"listenToKeyboardEvents()\"\n  (mouseleave)=\"unsubscribeFromKeyboardEvents(tableRef)\"\n>\n  <thead>\n    <tr\n      *ngIf=\"{\n        config: (tableConfig$ | async)!,\n        isLoading: loading$ | async\n      } as table\"\n    >\n      <ng-container\n        *ngFor=\"let column of table.config?.columns | keyvalue: columnOrder\"\n      >\n        <th\n          *ngIf=\"!column.value?.hidden\"\n          ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n            column.value.class\n          }}\"\n          [class.disabled]=\"table.isLoading\"\n          [attr.aria-sort]=\"sortOrder$ | async | sortClass: column.key:'aria'\"\n          [class.gt-sortable]=\"true\"\n          scope=\"col\"\n        >\n          <button\n            *ngIf=\"column.value?.sortable\"\n            [attr.data-sort-order]=\"\n              sortOrder$ | async | sortClass: column.key:'order'\n            \"\n            class=\"gt-sort\"\n            (click)=\"\n              table.isLoading ||\n                !column.value.sortable ||\n                sortByKey(column.key, $event)\n            \"\n          >\n            <span *ngIf=\"column.value?.header !== false\">{{\n              column.value.header || column.key | capitalCase\n            }}</span>\n          </button>\n          <span\n            *ngIf=\"!column.value?.sortable && column.value?.header !== false\"\n            >{{ column.value.header || column.key | capitalCase }}</span\n          >\n        </th>\n      </ng-container>\n      <ng-container\n        *ngIf=\"\n          ((table?.config?.rows | keyvalue: columnOrder) || [])[0] as headerRow\n        \"\n      >\n        <th\n          class=\"row-header\"\n          [attr.aria-sort]=\"\n            sortOrder$ | async | sortClass: headerRow.key:'aria'\n          \"\n          ngClass=\"{{ headerRow.value.sortable ? 'sort ' : '' }} {{\n            sortOrder$ | async | sortClass: headerRow.key\n          }} {{ (headerRow.key | dashCase) + '-column' }}\"\n          (click)=\"\n            table.isLoading ||\n              !headerRow.value.sortable ||\n              sortByKey(headerRow.key, $event)\n          \"\n          scope=\"col\"\n        >\n          <ng-container *ngIf=\"headerRow?.value?.header !== false\">{{\n            headerRow?.value?.header || headerRow.key | capitalCase\n          }}</ng-container>\n        </th>\n        <th\n          *ngFor=\"let column of ((table$ | async)?.data || [])[0]\"\n          ngClass=\"{{ headerRow.value.class }}\"\n        >\n          <ng-container\n            [ngTemplateOutlet]=\"\n              (table.config.rows || {})[headerRow.key].templateRef\n                ? templateRef\n                : (table.config.rows || {})[headerRow.key].transform\n                ? transformData\n                : rawData\n            \"\n            [ngTemplateOutletContext]=\"{\n              row: column,\n              column: headerRow,\n              transform: (table.config.rows || {})[headerRow.key].transform,\n              templateRef: (table.config.rows || {})[headerRow.key].templateRef,\n              index: 0\n            }\"\n          >\n          </ng-container>\n        </th>\n      </ng-container>\n    </tr>\n  </thead>\n  <tbody *ngIf=\"loading$ | async; else tableContent\">\n    <tr>\n      <td class=\"p-0\" [colSpan]=\"colspan$ | async\">\n        <ng-content select=\".table-loading\"></ng-content>\n      </td>\n    </tr>\n  </tbody>\n  <tfoot *ngIf=\"(table$ | async)! as table\">\n    <ng-container *ngIf=\"table.data.length > 0 && !(loading$ | async)\">\n      <ng-container *ngIf=\"(calculations$ | async)! as calculations\">\n        <tr\n          *ngFor=\"let calculation of calculations.calculations; let i = index\"\n        >\n          <ng-container\n            *ngIf=\"{\n              showHeader: (colspan$ | async) !== (footerColspan$ | async)\n            } as footerRow\"\n          >\n            <th\n              *ngIf=\"footerRow.showHeader\"\n              [colSpan]=\"\n                ((colspan$ | async) || 0) - ((footerColspan$ | async) || 0)\n              \"\n              scope=\"row\"\n            >\n              <ng-container\n                *ngIf=\"table.config?.footer?.headers?.[calculation] as showHeader\"\n                >{{showHeader === true ? (calculation | capitalCase): table.config.footer?.headers?.[calculation]}}\n              </ng-container>\n            </th>\n            <ng-container\n              *ngFor=\"\n                let column of table.config?.columns | keyvalue: columnOrder\n              \"\n            >\n              <td\n                *ngIf=\"\n                  !column.value?.hidden && calculations.calculated[column.key]\n                \"\n                ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n                  column.value.class\n                }}\"\n                [attr.data-header]=\"\n                  !footerRow.showHeader && table.config.footer?.headers?.[calculation]\n                    ? table.config.footer?.headers?.[calculation] === true ? (calculation | capitalCase) : table.config.footer?.headers?.[calculation]\n                    : null\n                \"\n                [attr.data-label]=\"\n                  table.config.mobileLayout && column.value.mobileHeader\n                    ? column.value.mobileHeader !== true\n                      ? column.value.mobileHeader\n                      : (column.value.header || column.key | capitalCase)\n                    : null\n                \"\n                [class.gt-no-content]=\"\n                  !calculations.calculated[column.key][calculation]\n                \"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"\n                    (table.config.columns || {})[column.key].templateRef\n                      ? templateRef\n                      : (table.config.columns || {})[column.key].transform\n                      ? transformFooter\n                      : rawFooter\n                  \"\n                  [ngTemplateOutletContext]=\"{\n                    value: calculations.calculated[column.key][calculation],\n                    row: calculations.calculated[column.key],\n                    column: calculation,\n                    templateRef: (table.config.columns || {})[column.key]\n                      .templateRef,\n                    transform: (table.config.columns || {})[column.key]\n                      .transform\n                  }\"\n                ></ng-container>\n              </td>\n            </ng-container>\n          </ng-container>\n        </tr>\n      </ng-container>\n    </ng-container>\n  </tfoot>\n</table>\n<ng-template #tableContent>\n  <ng-container *ngIf=\"(table$ | async)! as table\">\n    <tbody *ngIf=\"(table!.data![0] || table!.data!).length > 0; else noData\">\n      <ng-container *ngIf=\"table.config.columns\">\n        <tr\n          *ngFor=\"\n            let row of table!.data![\n              table.info.lazyLoaded ? 0 : (currentPaginationIndex$ | async) || 0\n            ];\n            trackBy: trackRowByFn;\n            let i = index\n          \"\n          [attr.id]=\"'tableRow_' + i\"\n          (click)=\"table?.config?.rowClick && _rowClick(row, i, $event)\"\n          (mouseenter)=\"\n            table?.config?.activateRowOnHover && _activateRow(row, i, $event)\n          \"\n          (mouseleave)=\"\n            table?.config?.activateRowOnHover &&\n              _activateRow(null, null, $event)\n          \"\n          [ngClass]=\"[\n            !!isRowSelectedFn\n              ? (row\n                | rowSelection\n                  : selection\n                  : isRowSelectedFn\n                  : customClasses.selectedRow)\n              : '',\n            (rowActive$ | async)?.index === i ? customClasses.activeRow : ''\n          ]\"\n        >\n          <ng-container\n            *ngFor=\"let column of table.config?.columns | keyvalue: columnOrder\"\n          >\n            <td\n              *ngIf=\"!column.value?.hidden\"\n              ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n                column.value.class\n              }}\"\n              [attr.data-label]=\"\n                table.config.mobileLayout && column.value.mobileHeader\n                  ? column.value.mobileHeader !== true\n                    ? column.value.mobileHeader\n                    : (column.value.header || column.key | capitalCase)\n                  : null\n              \"\n            >\n              <ng-container\n                [ngTemplateOutlet]=\"\n                  (searchBy$ | async) &&\n                  !(table.config.columns || {})[column.key].templateRef\n                    ? highlighted\n                    : (table.config.columns || {})[column.key].templateRef\n                    ? templateRef\n                    : (table.config.columns || {})[column.key].transform\n                    ? transformData\n                    : rawData\n                \"\n                [ngTemplateOutletContext]=\"{\n                  row: row,\n                  column: column,\n                  search: (searchBy$ | async),\n                  transform: (table.config.columns || {})[column.key].transform,\n                  templateRef: (table.config.columns || {})[column.key]\n                    .templateRef,\n                  index: i,\n                  data: table.data[\n                    table.info.lazyLoaded\n                      ? 0\n                      : (currentPaginationIndex$ | async) || 0\n                  ]\n                }\"\n              ></ng-container>\n            </td>\n          </ng-container>\n        </tr>\n      </ng-container>\n      <ng-container *ngIf=\"table.config.rows\">\n        <ng-container\n          *ngFor=\"\n            let row of table?.config?.rows | keyvalue: columnOrder | slice: 1;\n            let i = index\n          \"\n        >\n          <tr\n            *ngIf=\"!row.value?.hidden\"\n            [attr.id]=\"'tableRow_' + i\"\n            ngClass=\"{{ (row.key | dashCase) + '-row' }}\"\n            (click)=\"table?.config?.rowClick && _rowClick(row, i, $event)\"\n            (mouseenter)=\"\n              table?.config?.activateRowOnHover && _activateRow(row, i, $event)\n            \"\n            (mouseleave)=\"\n              table?.config?.activateRowOnHover &&\n                _activateRow(null, null, $event)\n            \"\n            [ngClass]=\"[\n              !!isRowSelectedFn\n                ? (row\n                  | rowSelection\n                    : selection\n                    : isRowSelectedFn\n                    : customClasses.selectedRow)\n                : '',\n              (rowActive$ | async)?.index === i ? customClasses.activeRow : ''\n            ]\"\n          >\n            <th class=\"row-header\" scope=\"row\">\n              {{ row.value.header || row.key | capitalCase }}\n            </th>\n            <td\n              *ngFor=\"let column of (table?.data || [])[0]; let y = index\"\n              ngClass=\"{{ row.value.class }}\"\n            >\n              <ng-container\n                [ngTemplateOutlet]=\"\n                  (table.config.rows || {})[row.key].templateRef\n                    ? templateRef\n                    : (table.config.rows || {})[row.key].transform\n                    ? transformData\n                    : rawData\n                \"\n                [ngTemplateOutletContext]=\"{\n                  row: column,\n                  column: row,\n                  transform: (table.config.rows || {})[row.key].transform,\n                  templateRef: (table.config.rows || {})[row.key].templateRef,\n                  index: table.config.rows ? y : i,\n                  data: table.data[\n                    table.info.lazyLoaded\n                      ? 0\n                      : (currentPaginationIndex$ | async) || 0\n                  ]\n                }\"\n              >\n              </ng-container>\n            </td>\n          </tr>\n        </ng-container>\n      </ng-container>\n    </tbody>\n  </ng-container>\n</ng-template>\n<ng-template #noData>\n  <tbody>\n    <tr>\n      <td class=\"p-0\" [colSpan]=\"colspan$ | async\">\n        <ng-content select=\".table-no-data\"></ng-content>\n      </td>\n    </tr>\n  </tbody>\n</ng-template>\n<ng-template\n  #highlighted\n  let-row=\"row\"\n  let-column=\"column\"\n  let-search=\"search\"\n  let-transform=\"transform\"\n>\n  <div\n    *ngIf=\"!transform\"\n    [innerHTML]=\"row[column.key] | highlight: search\"\n  ></div>\n  <div\n    *ngIf=\"transform\"\n    [innerHTML]=\"\n      row[column.key]\n        | dynamicPipe: transform.pipe:transform?.args\n        | highlight: search\n    \"\n  ></div>\n</ng-template>\n<ng-template #rawData let-row=\"row\" let-column=\"column\">\n  {{ row[column.key] }}\n</ng-template>\n<ng-template\n  #transformData\n  let-row=\"row\"\n  let-column=\"column\"\n  let-transform=\"transform\"\n  let-data=\"data\"\n>\n  {{ row[column.key] | dynamicPipe: transform.pipe:transform?.args }}\n</ng-template>\n<ng-template #transformFooter let-value=\"value\" let-transform=\"transform\">\n  {{\n    (value | dynamicPipe: transform.pipe:transform?.args) ||\n      (tableConfig$ | async)?.footer?.emptyContent\n  }}\n</ng-template>\n<ng-template #rawFooter let-value=\"value\">\n  {{ value || (tableConfig$ | async)?.footer?.emptyContent }}\n</ng-template>\n<ng-template\n  #templateRef\n  let-row=\"row\"\n  let-column=\"column\"\n  let-index=\"index\"\n  let-templateRef=\"templateRef\"\n  let-data=\"data\"\n>\n  <ng-container\n    [ngTemplateOutlet]=\"templateRef\"\n    [ngTemplateOutletContext]=\"{\n      row: row,\n      col: column,\n      index: index,\n      data: data\n    }\"\n  ></ng-container>\n</ng-template>\n", dependencies: [{ kind: "pipe", type: CapitalCasePipe, name: "capitalCase" }, { kind: "pipe", type: KeyValuePipe, name: "keyvalue" }, { kind: "pipe", type: SortClassPipe, name: "sortClass" }, { kind: "pipe", type: DashCasePipe, name: "dashCase" }, { kind: "pipe", type: RowSelectionPipe, name: "rowSelection" }, { kind: "pipe", type: AsyncPipe, name: "async" }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: SlicePipe, name: "slice" }, { kind: "pipe", type: DynamicPipe, name: "dynamicPipe" }, { kind: "pipe", type: HighlightPipe, name: "highlight" }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: CoreComponent, decorators: [{
            type: Component,
            args: [{ selector: 'angular-generic-table', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [
                        CapitalCasePipe,
                        KeyValuePipe,
                        SortClassPipe,
                        DashCasePipe,
                        RowSelectionPipe,
                        AsyncPipe,
                        NgTemplateOutlet,
                        SlicePipe,
                        DynamicPipe,
                        HighlightPipe,
                        NgClass,
                        NgIf,
                        NgForOf,
                    ], template: "<table\n  [ngClass]=\"(tableConfig$ | async)?.class || 'table'\"\n  [class.table-mobile]=\"(tableConfig$ | async)?.mobileLayout\"\n  [class.table-horizontal]=\"(tableConfig$ | async)?.rows\"\n  [class.table-loading]=\"loading$ | async\"\n  [class.gt-sticky-row-header]=\"\n    (tableConfig$ | async)?.stickyHeaders?.row && (tableConfig$ | async)?.rows\n  \"\n  [class.gt-sticky-column-header]=\"\n    (tableConfig$ | async)?.stickyHeaders?.column\n  \"\n  [attr.aria-busy]=\"(loading$ | async) === true ? true : null\"\n  [tabindex]=\"(tableConfig$ | async)?.activateRowOnKeyboardNavigation ? 0 : -1\"\n  #tableRef\n  (focus)=\"listenToKeyboardEvents()\"\n  (focusout)=\"unsubscribeFromKeyboardEvents(tableRef)\"\n  (mouseenter)=\"listenToKeyboardEvents()\"\n  (mouseleave)=\"unsubscribeFromKeyboardEvents(tableRef)\"\n>\n  <thead>\n    <tr\n      *ngIf=\"{\n        config: (tableConfig$ | async)!,\n        isLoading: loading$ | async\n      } as table\"\n    >\n      <ng-container\n        *ngFor=\"let column of table.config?.columns | keyvalue: columnOrder\"\n      >\n        <th\n          *ngIf=\"!column.value?.hidden\"\n          ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n            column.value.class\n          }}\"\n          [class.disabled]=\"table.isLoading\"\n          [attr.aria-sort]=\"sortOrder$ | async | sortClass: column.key:'aria'\"\n          [class.gt-sortable]=\"true\"\n          scope=\"col\"\n        >\n          <button\n            *ngIf=\"column.value?.sortable\"\n            [attr.data-sort-order]=\"\n              sortOrder$ | async | sortClass: column.key:'order'\n            \"\n            class=\"gt-sort\"\n            (click)=\"\n              table.isLoading ||\n                !column.value.sortable ||\n                sortByKey(column.key, $event)\n            \"\n          >\n            <span *ngIf=\"column.value?.header !== false\">{{\n              column.value.header || column.key | capitalCase\n            }}</span>\n          </button>\n          <span\n            *ngIf=\"!column.value?.sortable && column.value?.header !== false\"\n            >{{ column.value.header || column.key | capitalCase }}</span\n          >\n        </th>\n      </ng-container>\n      <ng-container\n        *ngIf=\"\n          ((table?.config?.rows | keyvalue: columnOrder) || [])[0] as headerRow\n        \"\n      >\n        <th\n          class=\"row-header\"\n          [attr.aria-sort]=\"\n            sortOrder$ | async | sortClass: headerRow.key:'aria'\n          \"\n          ngClass=\"{{ headerRow.value.sortable ? 'sort ' : '' }} {{\n            sortOrder$ | async | sortClass: headerRow.key\n          }} {{ (headerRow.key | dashCase) + '-column' }}\"\n          (click)=\"\n            table.isLoading ||\n              !headerRow.value.sortable ||\n              sortByKey(headerRow.key, $event)\n          \"\n          scope=\"col\"\n        >\n          <ng-container *ngIf=\"headerRow?.value?.header !== false\">{{\n            headerRow?.value?.header || headerRow.key | capitalCase\n          }}</ng-container>\n        </th>\n        <th\n          *ngFor=\"let column of ((table$ | async)?.data || [])[0]\"\n          ngClass=\"{{ headerRow.value.class }}\"\n        >\n          <ng-container\n            [ngTemplateOutlet]=\"\n              (table.config.rows || {})[headerRow.key].templateRef\n                ? templateRef\n                : (table.config.rows || {})[headerRow.key].transform\n                ? transformData\n                : rawData\n            \"\n            [ngTemplateOutletContext]=\"{\n              row: column,\n              column: headerRow,\n              transform: (table.config.rows || {})[headerRow.key].transform,\n              templateRef: (table.config.rows || {})[headerRow.key].templateRef,\n              index: 0\n            }\"\n          >\n          </ng-container>\n        </th>\n      </ng-container>\n    </tr>\n  </thead>\n  <tbody *ngIf=\"loading$ | async; else tableContent\">\n    <tr>\n      <td class=\"p-0\" [colSpan]=\"colspan$ | async\">\n        <ng-content select=\".table-loading\"></ng-content>\n      </td>\n    </tr>\n  </tbody>\n  <tfoot *ngIf=\"(table$ | async)! as table\">\n    <ng-container *ngIf=\"table.data.length > 0 && !(loading$ | async)\">\n      <ng-container *ngIf=\"(calculations$ | async)! as calculations\">\n        <tr\n          *ngFor=\"let calculation of calculations.calculations; let i = index\"\n        >\n          <ng-container\n            *ngIf=\"{\n              showHeader: (colspan$ | async) !== (footerColspan$ | async)\n            } as footerRow\"\n          >\n            <th\n              *ngIf=\"footerRow.showHeader\"\n              [colSpan]=\"\n                ((colspan$ | async) || 0) - ((footerColspan$ | async) || 0)\n              \"\n              scope=\"row\"\n            >\n              <ng-container\n                *ngIf=\"table.config?.footer?.headers?.[calculation] as showHeader\"\n                >{{showHeader === true ? (calculation | capitalCase): table.config.footer?.headers?.[calculation]}}\n              </ng-container>\n            </th>\n            <ng-container\n              *ngFor=\"\n                let column of table.config?.columns | keyvalue: columnOrder\n              \"\n            >\n              <td\n                *ngIf=\"\n                  !column.value?.hidden && calculations.calculated[column.key]\n                \"\n                ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n                  column.value.class\n                }}\"\n                [attr.data-header]=\"\n                  !footerRow.showHeader && table.config.footer?.headers?.[calculation]\n                    ? table.config.footer?.headers?.[calculation] === true ? (calculation | capitalCase) : table.config.footer?.headers?.[calculation]\n                    : null\n                \"\n                [attr.data-label]=\"\n                  table.config.mobileLayout && column.value.mobileHeader\n                    ? column.value.mobileHeader !== true\n                      ? column.value.mobileHeader\n                      : (column.value.header || column.key | capitalCase)\n                    : null\n                \"\n                [class.gt-no-content]=\"\n                  !calculations.calculated[column.key][calculation]\n                \"\n              >\n                <ng-container\n                  [ngTemplateOutlet]=\"\n                    (table.config.columns || {})[column.key].templateRef\n                      ? templateRef\n                      : (table.config.columns || {})[column.key].transform\n                      ? transformFooter\n                      : rawFooter\n                  \"\n                  [ngTemplateOutletContext]=\"{\n                    value: calculations.calculated[column.key][calculation],\n                    row: calculations.calculated[column.key],\n                    column: calculation,\n                    templateRef: (table.config.columns || {})[column.key]\n                      .templateRef,\n                    transform: (table.config.columns || {})[column.key]\n                      .transform\n                  }\"\n                ></ng-container>\n              </td>\n            </ng-container>\n          </ng-container>\n        </tr>\n      </ng-container>\n    </ng-container>\n  </tfoot>\n</table>\n<ng-template #tableContent>\n  <ng-container *ngIf=\"(table$ | async)! as table\">\n    <tbody *ngIf=\"(table!.data![0] || table!.data!).length > 0; else noData\">\n      <ng-container *ngIf=\"table.config.columns\">\n        <tr\n          *ngFor=\"\n            let row of table!.data![\n              table.info.lazyLoaded ? 0 : (currentPaginationIndex$ | async) || 0\n            ];\n            trackBy: trackRowByFn;\n            let i = index\n          \"\n          [attr.id]=\"'tableRow_' + i\"\n          (click)=\"table?.config?.rowClick && _rowClick(row, i, $event)\"\n          (mouseenter)=\"\n            table?.config?.activateRowOnHover && _activateRow(row, i, $event)\n          \"\n          (mouseleave)=\"\n            table?.config?.activateRowOnHover &&\n              _activateRow(null, null, $event)\n          \"\n          [ngClass]=\"[\n            !!isRowSelectedFn\n              ? (row\n                | rowSelection\n                  : selection\n                  : isRowSelectedFn\n                  : customClasses.selectedRow)\n              : '',\n            (rowActive$ | async)?.index === i ? customClasses.activeRow : ''\n          ]\"\n        >\n          <ng-container\n            *ngFor=\"let column of table.config?.columns | keyvalue: columnOrder\"\n          >\n            <td\n              *ngIf=\"!column.value?.hidden\"\n              ngClass=\"{{ (column.key | dashCase) + '-column' }} {{\n                column.value.class\n              }}\"\n              [attr.data-label]=\"\n                table.config.mobileLayout && column.value.mobileHeader\n                  ? column.value.mobileHeader !== true\n                    ? column.value.mobileHeader\n                    : (column.value.header || column.key | capitalCase)\n                  : null\n              \"\n            >\n              <ng-container\n                [ngTemplateOutlet]=\"\n                  (searchBy$ | async) &&\n                  !(table.config.columns || {})[column.key].templateRef\n                    ? highlighted\n                    : (table.config.columns || {})[column.key].templateRef\n                    ? templateRef\n                    : (table.config.columns || {})[column.key].transform\n                    ? transformData\n                    : rawData\n                \"\n                [ngTemplateOutletContext]=\"{\n                  row: row,\n                  column: column,\n                  search: (searchBy$ | async),\n                  transform: (table.config.columns || {})[column.key].transform,\n                  templateRef: (table.config.columns || {})[column.key]\n                    .templateRef,\n                  index: i,\n                  data: table.data[\n                    table.info.lazyLoaded\n                      ? 0\n                      : (currentPaginationIndex$ | async) || 0\n                  ]\n                }\"\n              ></ng-container>\n            </td>\n          </ng-container>\n        </tr>\n      </ng-container>\n      <ng-container *ngIf=\"table.config.rows\">\n        <ng-container\n          *ngFor=\"\n            let row of table?.config?.rows | keyvalue: columnOrder | slice: 1;\n            let i = index\n          \"\n        >\n          <tr\n            *ngIf=\"!row.value?.hidden\"\n            [attr.id]=\"'tableRow_' + i\"\n            ngClass=\"{{ (row.key | dashCase) + '-row' }}\"\n            (click)=\"table?.config?.rowClick && _rowClick(row, i, $event)\"\n            (mouseenter)=\"\n              table?.config?.activateRowOnHover && _activateRow(row, i, $event)\n            \"\n            (mouseleave)=\"\n              table?.config?.activateRowOnHover &&\n                _activateRow(null, null, $event)\n            \"\n            [ngClass]=\"[\n              !!isRowSelectedFn\n                ? (row\n                  | rowSelection\n                    : selection\n                    : isRowSelectedFn\n                    : customClasses.selectedRow)\n                : '',\n              (rowActive$ | async)?.index === i ? customClasses.activeRow : ''\n            ]\"\n          >\n            <th class=\"row-header\" scope=\"row\">\n              {{ row.value.header || row.key | capitalCase }}\n            </th>\n            <td\n              *ngFor=\"let column of (table?.data || [])[0]; let y = index\"\n              ngClass=\"{{ row.value.class }}\"\n            >\n              <ng-container\n                [ngTemplateOutlet]=\"\n                  (table.config.rows || {})[row.key].templateRef\n                    ? templateRef\n                    : (table.config.rows || {})[row.key].transform\n                    ? transformData\n                    : rawData\n                \"\n                [ngTemplateOutletContext]=\"{\n                  row: column,\n                  column: row,\n                  transform: (table.config.rows || {})[row.key].transform,\n                  templateRef: (table.config.rows || {})[row.key].templateRef,\n                  index: table.config.rows ? y : i,\n                  data: table.data[\n                    table.info.lazyLoaded\n                      ? 0\n                      : (currentPaginationIndex$ | async) || 0\n                  ]\n                }\"\n              >\n              </ng-container>\n            </td>\n          </tr>\n        </ng-container>\n      </ng-container>\n    </tbody>\n  </ng-container>\n</ng-template>\n<ng-template #noData>\n  <tbody>\n    <tr>\n      <td class=\"p-0\" [colSpan]=\"colspan$ | async\">\n        <ng-content select=\".table-no-data\"></ng-content>\n      </td>\n    </tr>\n  </tbody>\n</ng-template>\n<ng-template\n  #highlighted\n  let-row=\"row\"\n  let-column=\"column\"\n  let-search=\"search\"\n  let-transform=\"transform\"\n>\n  <div\n    *ngIf=\"!transform\"\n    [innerHTML]=\"row[column.key] | highlight: search\"\n  ></div>\n  <div\n    *ngIf=\"transform\"\n    [innerHTML]=\"\n      row[column.key]\n        | dynamicPipe: transform.pipe:transform?.args\n        | highlight: search\n    \"\n  ></div>\n</ng-template>\n<ng-template #rawData let-row=\"row\" let-column=\"column\">\n  {{ row[column.key] }}\n</ng-template>\n<ng-template\n  #transformData\n  let-row=\"row\"\n  let-column=\"column\"\n  let-transform=\"transform\"\n  let-data=\"data\"\n>\n  {{ row[column.key] | dynamicPipe: transform.pipe:transform?.args }}\n</ng-template>\n<ng-template #transformFooter let-value=\"value\" let-transform=\"transform\">\n  {{\n    (value | dynamicPipe: transform.pipe:transform?.args) ||\n      (tableConfig$ | async)?.footer?.emptyContent\n  }}\n</ng-template>\n<ng-template #rawFooter let-value=\"value\">\n  {{ value || (tableConfig$ | async)?.footer?.emptyContent }}\n</ng-template>\n<ng-template\n  #templateRef\n  let-row=\"row\"\n  let-column=\"column\"\n  let-index=\"index\"\n  let-templateRef=\"templateRef\"\n  let-data=\"data\"\n>\n  <ng-container\n    [ngTemplateOutlet]=\"templateRef\"\n    [ngTemplateOutletContext]=\"{\n      row: row,\n      col: column,\n      index: index,\n      data: data\n    }\"\n  ></ng-container>\n</ng-template>\n" }]
        }], propDecorators: { navigationKeys: [{
                type: Input
            }], selectKeys: [{
                type: Input
            }], loading: [{
                type: Input
            }], paginationIndex: [{
                type: Input
            }], pagingInfo: [{
                type: Input
            }], customClasses: [{
                type: Input
            }], isRowSelectedFn: [{
                type: Input
            }], selection: [{
                type: Input
            }], rowIdKey: [{
                type: Input
            }], generateRowId: [{
                type: Input
            }], trackRowByFn: [{
                type: Input
            }], search: [{
                type: Input
            }], config: [{
                type: Input
            }], data: [{
                type: Input
            }], sortOrder: [{
                type: Input
            }], rowClick: [{
                type: Output
            }], rowSelect: [{
                type: Output
            }], sortOrderChange: [{
                type: Output
            }], rowActive: [{
                type: Output
            }], columnSort: [{
                type: Output
            }], pageChange: [{
                type: Output
            }] } });

class GtDeltaComponent {
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
    }
    get deltaTemplate() {
        return this._deltaTemplate;
    }
    constructor() {
        this.Math = Math;
        this.Number = Number;
        this.data = [];
        this.index = 0;
        this.classes = {
            span: 'gt-delta',
            positive: 'text-success',
            negative: 'text-danger',
        };
        this.key = 'value';
        this.notApplicableValue = null;
        this.initialValue = null;
    }
    set deltaTemplate(deltaTemplate) {
        this._deltaTemplate = deltaTemplate;
    }
    ngOnChanges(changes) {
        if (!changes.data.currentValue) {
            return;
        }
        const data = changes.data?.currentValue;
        const index = changes.index?.currentValue || this.index;
        const baseIndex = changes.baseIndex?.currentValue;
        const key = changes.key?.currentValue || this.key;
        const initialValue = changes.initialValue?.currentValue || this.initialValue;
        const deltaValue = index === 0
            ? initialValue
            : data[index][key] -
                (baseIndex === undefined
                    ? data[index - 1][key]
                    : data[baseIndex][key]);
        const baseValue = index === 0
            ? 1
            : baseIndex === undefined
                ? data[index - 1][key]
                : data[baseIndex][key];
        const relative = index === 0
            ? initialValue
            : Math.sign(deltaValue) * Math.abs(deltaValue / baseValue);
        this.value = {
            relative: Number.isFinite(relative)
                ? relative
                : index === 0
                    ? initialValue
                    : changes.notApplicableValue?.currentValue || this.notApplicableValue,
            absolute: deltaValue,
        };
    }
}
GtDeltaComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GtDeltaComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
GtDeltaComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.1", type: GtDeltaComponent, isStandalone: true, selector: "gt-delta", inputs: { deltaTemplate: "deltaTemplate", data: "data", index: "index", baseIndex: "baseIndex", classes: "classes", key: "key", notApplicableValue: "notApplicableValue", initialValue: "initialValue" }, usesOnChanges: true, ngImport: i0, template: `<span
      *ngIf="value as delta"
      [class]="[
        classes.span,
        delta.absolute > 0
          ? classes.positive
          : delta.absolute < 0
          ? classes.negative
          : null
      ]"
      [class.gt-delta-positive]="delta.absolute > 0"
      [class.gt-delta-negative]="delta.absolute < 0"
      ><ng-container
        *ngTemplateOutlet="deltaTemplate || defaultTemplate; context: { delta }"
      ></ng-container>
    </span>
    <ng-template #defaultTemplate let-delta="delta">
      <span *ngIf="delta.relative">{{
        delta.relative | percent: '1.0-2'
      }}</span>
    </ng-template>`, isInline: true, styles: [":host{display:inline-block}\n"], dependencies: [{ kind: "pipe", type: PercentPipe, name: "percent" }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GtDeltaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gt-delta', template: `<span
      *ngIf="value as delta"
      [class]="[
        classes.span,
        delta.absolute > 0
          ? classes.positive
          : delta.absolute < 0
          ? classes.negative
          : null
      ]"
      [class.gt-delta-positive]="delta.absolute > 0"
      [class.gt-delta-negative]="delta.absolute < 0"
      ><ng-container
        *ngTemplateOutlet="deltaTemplate || defaultTemplate; context: { delta }"
      ></ng-container>
    </span>
    <ng-template #defaultTemplate let-delta="delta">
      <span *ngIf="delta.relative">{{
        delta.relative | percent: '1.0-2'
      }}</span>
    </ng-template>`, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [PercentPipe, NgIf, NgTemplateOutlet], styles: [":host{display:inline-block}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { deltaTemplate: [{
                type: Input
            }], data: [{
                type: Input
            }], index: [{
                type: Input
            }], baseIndex: [{
                type: Input
            }], classes: [{
                type: Input
            }], key: [{
                type: Input
            }], notApplicableValue: [{
                type: Input
            }], initialValue: [{
                type: Input
            }] } });

class GenericTableCoreModule {
}
GenericTableCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTableCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GenericTableCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: GenericTableCoreModule, imports: [CommonModule,
        CoreComponent,
        SortClassPipe,
        DashCasePipe,
        HighlightPipe,
        RowSelectionPipe,
        CapitalCasePipe,
        CapitalCasePipe,
        DynamicPipe,
        GtDeltaComponent], exports: [CoreComponent, GtDeltaComponent] });
GenericTableCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTableCoreModule, imports: [CommonModule,
        CoreComponent,
        GtDeltaComponent] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTableCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CoreComponent,
                        SortClassPipe,
                        DashCasePipe,
                        HighlightPipe,
                        RowSelectionPipe,
                        CapitalCasePipe,
                        CapitalCasePipe,
                        DynamicPipe,
                        GtDeltaComponent,
                    ],
                    exports: [CoreComponent, GtDeltaComponent],
                    declarations: [],
                }]
        }] });

class PaginationComponent {
    constructor() {
        this._table$ = new ReplaySubject(1);
        this._ariaLabels = {
            nav: 'Table pagination',
            button: 'Go to page ',
        };
        this._classes = {
            ul: 'pagination',
            li: 'page-item',
            button: 'page-link',
        };
        this._paginationLength = 5;
        /** paginationListItems$ - observable for page numbers to show based on number of pages and current position  */
        this.paginationListItems$ = this._table$.pipe(switchMap((core) => combineLatest([
            core?.table$.pipe(pluck('info')),
            core?.currentPaginationIndex$,
        ])), map(([info, currentPage]) => this._generateList(info.pageTotal, currentPage)), shareReplay(1));
    }
    get pagingInfo() {
        return (this._pagingInfo || {
            pageNext: null,
            pageCurrent: null,
            pagePrevious: null,
            pageSize: null,
            numberOfRecords: null,
            pageTotal: null,
        });
    }
    /** pagingInfo
     * @description when provided, pagination component will use this information to render pagination instead of data from table. Use this option when pagination is handled by backend (server side).
     * @type info - metadata for pagination component
     */
    set pagingInfo(info) {
        this._pagingInfo = info;
    }
    get paginationLength() {
        return this._paginationLength;
    }
    /** paginationLength
     * @description number of buttons to show in pagination
     * @type length - number of buttons to show. Defaults to: `5`
     */
    set paginationLength(length) {
        this._paginationLength = +length;
    }
    get classes() {
        return this._classes;
    }
    /** classes
     * @description classes that should be used within pagination component for different elements
     * @type classes - classes to be used. Defaults to: `{
     *     ul: 'pagination',
     *     li: 'page-item',
     *     button: 'page-link',
     *   }`
     */
    set classes(classes) {
        this._classes = classes;
    }
    get ariaLabels() {
        return this._ariaLabels;
    }
    /** ariaLabels
     * @description aria labels that describe pagination component
     * @type labels - aria labels for pagination. Defaults to: `{
     *     nav: 'Table pagination',
     *     button: 'Go to page ',
     *   }`
     */
    set ariaLabels(labels) {
        this._ariaLabels = labels;
    }
    get table() {
        return this._table;
    }
    /** table
     * @description table component to which pagination is attached
     * @type tableRef - table component
     */
    set table(tableRef) {
        this._table = tableRef;
        this._table$.next(tableRef);
    }
    /** generate list - generate an array with page numbers to show based on number of pages and current position
     * @param numberOfPages number of pages to show
     * @param currentPosition current position (page index) being shown in table
     * @returns Array<number> array of page numbers to show
     */
    _generateList(numberOfPages, currentPosition) {
        const middle = Math.floor(this.paginationLength / 2);
        const length = numberOfPages < this.paginationLength
            ? numberOfPages
            : this.paginationLength;
        return Array.from({ length }, (_, i) => {
            if (i === 0) {
                return 1;
            }
            else if (numberOfPages < this.paginationLength) {
                return i + 1;
            }
            else if (i + 1 === length) {
                return numberOfPages;
            }
            else if (currentPosition > middle &&
                currentPosition < numberOfPages - middle) {
                return i + currentPosition - (middle - 1);
            }
            else if (currentPosition > middle &&
                currentPosition < numberOfPages - (middle - 1)) {
                return i + currentPosition - middle;
            }
            else if (currentPosition > middle &&
                currentPosition === numberOfPages - (middle - 1)) {
                return i + currentPosition - (middle + 1);
            }
            else if (currentPosition > middle &&
                currentPosition === numberOfPages - 1) {
                return i + currentPosition - (middle + 2);
            }
            else {
                return i + 1;
            }
        });
    }
    /** go to page
     * @param index - page index to go to
     */
    goToPage(index) {
        if (this.table) {
            this.table.paginationIndex = index - 1;
        }
    }
}
PaginationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: PaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PaginationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.1", type: PaginationComponent, isStandalone: true, selector: "angular-generic-table-pagination", inputs: { pagingInfo: "pagingInfo", paginationLength: "paginationLength", classes: "classes", ariaLabels: "ariaLabels", table: "table" }, ngImport: i0, template: "<ng-container\n  *ngIf=\"{\n    links: paginationListItems$ | async,\n    currentPosition: table?.currentPaginationIndex$ | async\n  } as pagination\"\n>\n  <nav\n    *ngIf=\"pagination.links && pagination.links.length > 1\"\n    role=\"navigation\"\n    [attr.aria-label]=\"ariaLabels.nav\"\n    class=\"gt-pagination\"\n    [class]=\"classes.nav\"\n  >\n    <ul [class]=\"classes.ul\">\n      <ng-container\n        *ngFor=\"\n          let position of pagination!.links;\n          let i = index;\n          let last = last\n        \"\n      >\n        <li\n          [class]=\"classes.li\"\n          [class.active]=\"position === (pagination!.currentPosition || 0) + 1\"\n        >\n          <button\n            [class]=\"classes.button\"\n            [attr.aria-label]=\"ariaLabels.button + position\"\n            (click)=\"goToPage(position)\"\n          >\n            {{ position }}\n          </button>\n        </li>\n        <li\n          [class]=\"classes.li\"\n          class=\"gt-ellipsis\"\n          *ngIf=\"position + 1 !== pagination!.links![i + 1] && !last\"\n        >\n          <button [class]=\"classes.button\" disabled tabindex=\"-1\"></button>\n        </li>\n      </ng-container>\n    </ul>\n  </nav>\n</ng-container>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'angular-generic-table-pagination', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule], template: "<ng-container\n  *ngIf=\"{\n    links: paginationListItems$ | async,\n    currentPosition: table?.currentPaginationIndex$ | async\n  } as pagination\"\n>\n  <nav\n    *ngIf=\"pagination.links && pagination.links.length > 1\"\n    role=\"navigation\"\n    [attr.aria-label]=\"ariaLabels.nav\"\n    class=\"gt-pagination\"\n    [class]=\"classes.nav\"\n  >\n    <ul [class]=\"classes.ul\">\n      <ng-container\n        *ngFor=\"\n          let position of pagination!.links;\n          let i = index;\n          let last = last\n        \"\n      >\n        <li\n          [class]=\"classes.li\"\n          [class.active]=\"position === (pagination!.currentPosition || 0) + 1\"\n        >\n          <button\n            [class]=\"classes.button\"\n            [attr.aria-label]=\"ariaLabels.button + position\"\n            (click)=\"goToPage(position)\"\n          >\n            {{ position }}\n          </button>\n        </li>\n        <li\n          [class]=\"classes.li\"\n          class=\"gt-ellipsis\"\n          *ngIf=\"position + 1 !== pagination!.links![i + 1] && !last\"\n        >\n          <button [class]=\"classes.button\" disabled tabindex=\"-1\"></button>\n        </li>\n      </ng-container>\n    </ul>\n  </nav>\n</ng-container>\n" }]
        }], propDecorators: { pagingInfo: [{
                type: Input
            }], paginationLength: [{
                type: Input
            }], classes: [{
                type: Input
            }], ariaLabels: [{
                type: Input
            }], table: [{
                type: Input
            }] } });

class GenericTablePaginationModule {
}
GenericTablePaginationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTablePaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GenericTablePaginationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.1", ngImport: i0, type: GenericTablePaginationModule, imports: [CommonModule, PaginationComponent], exports: [PaginationComponent] });
GenericTablePaginationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTablePaginationModule, imports: [CommonModule, PaginationComponent] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.1", ngImport: i0, type: GenericTablePaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PaginationComponent],
                    exports: [PaginationComponent],
                }]
        }] });

/*
 * Public API Surface of core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CapitalCasePipe, CoreComponent, CoreService, DashCasePipe, DynamicPipe, GenericTableCoreModule, GenericTablePaginationModule, GtDeltaComponent, HighlightPipe, PaginationComponent, SortClassPipe, calculate, capitalize, chunk, dashed, parseSortOrderParams, search, sortOnMultipleKeys, sortOrderConfigToParam, sortOrderToParams };
